import React, { useContext } from "react"
import { View, Text, StyleSheet } from "react-native"
import type { StyleProp, ViewStyle } from "react-native"
import type { SelectedIcon as ISelectedIcon } from "@ecountabl/lib"
import SelectedIcon from "./selected-icon"
import { ThemeContext } from "./context/ThemeContext"

const isHexColor = (c: string | undefined): c is string =>
  !!c && /^#[0-9a-fA-F]{6}$/.test(c)

export type ValueBadgeProps = {
  /** Solid fill when enabled; not used when `disabled` */
  color?: string
  /** When `color` is missing or not `#RRGGBB` */
  accentFallbackColor?: string
  icon?: ISelectedIcon | null
  /** Shown as a single letter when there is no icon */
  label?: string
  disabled?: boolean
  /** Icon and letter color when disabled; defaults to theme `textAlt` */
  disabledColor?: string
  size?: number
  iconSize?: number
  style?: StyleProp<ViewStyle>
  accessibilityLabel?: string
}

/**
 * Circular value badge: solid value color + white glyph when enabled;
 * transparent + `disabledColor` glyph when disabled. No border.
 */
export const ValueBadge: React.FC<ValueBadgeProps> = ({
  color,
  accentFallbackColor,
  icon,
  label,
  disabled = false,
  disabledColor: disabledColorOverride,
  size = 28,
  iconSize,
  style,
  accessibilityLabel,
}: ValueBadgeProps) => {
  const theme = useContext(ThemeContext).theme
  const { primary, white, textAlt } = theme.colors
  const disabledColor = disabledColorOverride ?? textAlt
  const fallback = accentFallbackColor ?? primary
  const bg = disabled ? "transparent" : (isHexColor(color) ? color : fallback)
  const glyph = disabled ? disabledColor : white
  const iSize = iconSize ?? Math.max(12, Math.round(size * 0.5))
  const letter = String(label || "?").trim().slice(0, 1).toUpperCase()
  const fontSize = Math.max(11, Math.round(size * 0.46))

  return (
    <View
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="image"
      style={[
        styles.badge,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: bg,
          borderWidth: disabled ? 1 : 0,
          borderColor: disabled ? disabledColor : "transparent",
        },
        style,
      ]}
    >
      {icon?.family && icon?.name ? (
        <SelectedIcon icon={icon} size={iSize} color={glyph} />
      ) : (
        <Text style={[styles.letter, { color: glyph, fontSize }]}>{letter}</Text>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  badge: {
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 0,
    overflow: "hidden",
  },
  letter: {
    fontWeight: "800",
  },
})

export default ValueBadge
